export type NavBadgeTone = "blue" | "green" | "amber" | "violet" | "slate";
export type DocsProductKey = "smart-ppt" | "smart-resume" | "smart-contract";

export interface NavItem {
  title: string;
  href?: string;
  items?: NavItem[];
  badge?: string;
  badgeTone?: NavBadgeTone;
  description?: string;
  ctaLabel?: string;
}

export const DOCS_PRODUCT_QUERY_PARAM = "product";
export const DEFAULT_DOCS_PRODUCT: DocsProductKey = "smart-ppt";

export const docsProducts: Array<{ key: DocsProductKey; label: string }> = [
  { key: "smart-ppt", label: "智能 PPT" },
  { key: "smart-resume", label: "智能简历" },
  { key: "smart-contract", label: "智能合同" },
];

export const commonNavigation: NavItem[] = [
  {
    title: "企业认证 & Token 获取方式",
    href: "/docs/preparation/enterprise-auth-api-key",
    description: "企业认证、应用创建与 Token 获取方式，三个产品通用",
  },
];

export const productNavigation: Record<DocsProductKey, NavItem[]> = {
  "smart-ppt": [
    { title: "产品介绍", href: "/docs/smart-ppt/guide/introduction" },
    { title: "能力全景", href: "/docs/smart-ppt/panorama" },
    {
      title: "选择接入方式",
      href: "/docs/smart-ppt/guide/choose-method",
      badge: "先读",
      badgeTone: "blue",
    },
    {
      href: "/docs/smart-ppt/api/overview",
      title: "API 直连方案",
      items: [
        { title: "接口说明与生成入口", href: "/docs/smart-ppt/api/overview" },
        { title: "大纲转 PPT", href: "/docs/smart-ppt/create/custom-outline" },
        { title: "PPT 下载", href: "/docs/smart-ppt/api/download" },
      ],
    },
    { title: "SaaS 嵌入方案", href: "/docs/smart-ppt/saas/guide" },
    { title: "OpenPPT 源码方案", href: "/docs/smart-ppt/open/introduction" },
    { title: "MCP Server 方案", href: "/docs/smart-ppt/mcp/mcp-server", badge: "Agent", badgeTone: "green" },
    { title: "收费标准", href: "/docs/smart-ppt/guide/pricing" },
  ],
  "smart-resume": [
    { title: "产品介绍", href: "/docs/smart-resume/guide/introduction" },
    { title: "选择接入方式", href: "/docs/smart-resume/guide/choose-method", badge: "先读", badgeTone: "blue" },
    {
      title: "API 直连方案",
      href: "/docs/smart-resume/api/overview",
      items: [
        { title: "简历生成", href: "/docs/smart-resume/api/generate" },
        { title: "简历解析", href: "/docs/smart-resume/api/parse" },
        { title: "简历分析", href: "/docs/smart-resume/api/analyze" },
      ],
    },
    { title: "收费标准", href: "/docs/smart-resume/guide/pricing" },
  ],
  "smart-contract": [
    { title: "产品介绍", href: "/docs/smart-contract/guide/introduction" },
    { title: "能力全景", href: "/docs/smart-contract/guide/panorama" },
    { title: "API 直连方案", href: "/docs/smart-contract/guide/api-direct" },
    { title: "收费标准", href: "/docs/smart-contract/guide/pricing" },
  ],
};

const smartPptSidebarNavigation: NavItem[] = [
  {
    title: "产品介绍",
    href: "/docs/smart-ppt/guide/introduction",
  },
  {
    title: "选择接入方式",
    href: "/docs/smart-ppt/guide/choose-method",
    badge: "先读",
    badgeTone: "blue",
  },
  {
    title: "企业认证 & Token 获取方式",
    href: "/docs/preparation/enterprise-auth-api-key",
    badge: "共用",
    badgeTone: "amber",
  },
  {
    title: "API 直连方案",
    items: [
      {
        title: "导读与流程",
        items: [
          { title: "API 接口说明", href: "/docs/smart-ppt/api/overview", badge: "先看", badgeTone: "blue" },
          { title: "基础接入示例流程", href: "/docs/smart-ppt/api/workflow", badge: "先看", badgeTone: "blue" },
          { title: "异步接口说明", href: "/docs/smart-ppt/quick-start/async-model" },
          { title: "二次处理与辅助能力", href: "/docs/smart-ppt/api/options" },
        ],
      },
      {
        title: "生成接口",
        items: [
          { title: "输入文字主题生成", href: "/docs/smart-ppt/create/text-topic" },
          { title: "上传文件生成（PDF / Word）", href: "/docs/smart-ppt/create/file-to-ppt" },
          { title: "答辩论文生成", href: "/docs/smart-ppt/create/thesis-defense" },
          { title: "绘图 PPT 生成", href: "/docs/smart-ppt/create/banana-generate-api" },
          { title: "自定义大纲生成", href: "/docs/smart-ppt/create/custom-outline" },
          { title: "Markdown 大纲生成", href: "/docs/smart-ppt/create/markdown-outline" },
          { title: "企业模板生成", href: "/docs/smart-ppt/create/company-template" },
        ],
      },
      {
        title: "扩展生成接口",
        items: [
          { title: "更多生成方案", href: "/docs/smart-ppt/create/more-scenarios", badge: "导读", badgeTone: "blue" },
          { title: "AI 标题大纲生成", href: "/docs/smart-ppt/create/outline-api" },
          { title: "模板封面生成", href: "/docs/smart-ppt/create/cover-api" },
        ],
      },
      {
        title: "结果与交付接口",
        items: [
          { title: "任务状态查询与结果获取", href: "/docs/smart-ppt/common/progress", badge: "必经", badgeTone: "green" },
          { title: "导出与下载", href: "/docs/smart-ppt/api/download" },
          { title: "打开编辑器", href: "/docs/smart-ppt/common/editor" },
        ],
      },
      {
        title: "增强接口",
        items: [
          { title: "PPT 二次处理", href: "/docs/smart-ppt/common/shared-services", badge: "依赖 PPT", badgeTone: "slate" },
          { title: "插入PPT单页", href: "/docs/smart-ppt/enhance/ppt-page", badge: "依赖 PPT", badgeTone: "slate" },
          { title: "添加PPT演讲稿", href: "/docs/smart-ppt/enhance/ppt-note", badge: "依赖 PPT", badgeTone: "slate" },
        ],
      },
    ],
  },
  {
    title: "SaaS 嵌入方案",
    items: [
      {
        title: "导读与接入方式",
        items: [
          { title: "接入总览", href: "/docs/smart-ppt/saas/guide", badge: "先看", badgeTone: "blue" },
          { title: "基础版接入（完整业务流程）", href: "/docs/smart-ppt/embed/basic-flow" },
          { title: "组件版接入（返回编辑器链接）", href: "/docs/smart-ppt/saas/extensions" },
        ],
      },
      {
        title: "授权与运营接口",
        items: [
          { title: "用户体系与事件回调", href: "/docs/smart-ppt/embed/operations" },
          { title: "用户管理 API", href: "/docs/smart-ppt/saas/users" },
          { title: "消息通知 API", href: "/docs/smart-ppt/saas/notifications" },
        ],
      },
    ],
  },
  {
    title: "OpenPPT 源码方案",
    items: [
      {
        title: "方案说明",
        items: [
          { title: "架构与依赖关系", href: "/docs/smart-ppt/mcp/open-ppt", badge: "先看", badgeTone: "blue" },
          { title: "产品介绍", href: "/docs/smart-ppt/open/introduction" },
        ],
      },
      {
        title: "接入指南",
        items: [{ title: "核心功能接入指南", href: "/docs/smart-ppt/open/guide" }],
      },
    ],
  },
  {
    title: "MCP Server 方案",
    badge: "Agent",
    badgeTone: "green",
    items: [
      { title: "工具列表与调用指南", href: "/docs/smart-ppt/mcp/mcp-server", badge: "先看", badgeTone: "blue" },
      { title: "Streamable HTTP 使用指南", href: "/docs/smart-ppt/mcp/streamable-http" },
    ],
  },
  {
    title: "收费标准",
    href: "/docs/smart-ppt/guide/pricing",
  },
];

const smartResumeSidebarNavigation: NavItem[] = [
  {
    title: "选择接入方式",
    href: "/docs/smart-resume/guide/choose-method",
    badge: "先读",
    badgeTone: "blue",
  },
  {
    title: "企业认证 & Token 获取方式",
    href: "/docs/preparation/enterprise-auth-api-key",
    badge: "共用",
    badgeTone: "amber",
  },
  {
    title: "API 直连方案",
    items: [
      {
        title: "导读与基础",
        items: [
          { title: "产品介绍", href: "/docs/smart-resume/guide/introduction" },
          { title: "能力全景与 API 导读", href: "/docs/smart-resume/api/overview", badge: "先看", badgeTone: "blue" },
        ],
      },
      {
        title: "生成与解析",
        items: [
          { title: "简历生成", href: "/docs/smart-resume/api/generate" },
          { title: "简历解析", href: "/docs/smart-resume/api/parse" },
        ],
      },
      {
        title: "评估与推荐",
        items: [
          { title: "人岗匹配", href: "/docs/smart-resume/api/match", badge: "异步", badgeTone: "green" },
          { title: "简历分析", href: "/docs/smart-resume/api/analyze", badge: "异步", badgeTone: "green" },
          { title: "岗位探测", href: "/docs/smart-resume/api/detect" },
        ],
      },
      {
        title: "内容增强与交付",
        items: [
          { title: "简历辅写", href: "/docs/smart-resume/api/write" },
          { title: "简历美化", href: "/docs/smart-resume/api/style" },
          { title: "简历范文", href: "/docs/smart-resume/api/examples" },
        ],
      },
    ],
  },
  {
    title: "收费标准",
    href: "/docs/smart-resume/guide/pricing",
  },
];

export function isDocsProductKey(value?: string | null): value is DocsProductKey {
  return docsProducts.some((item) => item.key === value);
}

export function detectDocsProductByHref(href: string): DocsProductKey | null {
  if (href.startsWith("/docs/smart-ppt")) {
    return "smart-ppt";
  }
  if (href.startsWith("/docs/smart-resume")) {
    return "smart-resume";
  }
  if (href.startsWith("/docs/smart-contract")) {
    return "smart-contract";
  }
  return null;
}

export function resolveDocsProduct(productParam?: string | null, href?: string): DocsProductKey {
  if (href) {
    const productFromHref = detectDocsProductByHref(href);
    if (productFromHref) {
      return productFromHref;
    }
  }

  if (isDocsProductKey(productParam)) {
    return productParam;
  }

  return DEFAULT_DOCS_PRODUCT;
}

export function getProductIntroHref(product: DocsProductKey): string {
  return productNavigation[product][0]?.href || "/docs/getting-started/introduction";
}

export function buildDocsHref(
  href: string,
  product: DocsProductKey,
  searchParams?: { toString(): string }
): string {
  const params = new URLSearchParams(searchParams?.toString() || "");
  params.set(DOCS_PRODUCT_QUERY_PARAM, product);

  const query = params.toString();
  return query ? `${href}?${query}` : href;
}

export function getDocsNavigation(product: DocsProductKey): NavItem[] {
  return [...commonNavigation, ...getRuntimeProductNavigation(product)];
}

export function getSidebarProductNavigation(product: DocsProductKey): NavItem[] {
  if (product === "smart-ppt") {
    return smartPptSidebarNavigation;
  }
  if (product === "smart-resume") {
    return smartResumeSidebarNavigation;
  }

  return productNavigation[product].map((item) => ({
    title: item.title,
    href: item.href || getFirstAvailableHref(item),
    badge: item.badge,
    badgeTone: item.badgeTone,
  }));
}

export function getAllDocsNavigation(): NavItem[] {
  return [
    ...commonNavigation,
    ...smartPptSidebarNavigation,
    ...smartResumeSidebarNavigation,
    ...productNavigation["smart-contract"],
  ];
}

export function isCommonHref(href: string): boolean {
  return getFlattenedNav(commonNavigation).some((item) => item.href === href);
}

export function isHrefInProductNavigation(product: DocsProductKey, href: string): boolean {
  return getFlattenedNav(getRuntimeProductNavigation(product)).some((item) => item.href === href);
}

export function getRuntimeProductNavigation(product: DocsProductKey): NavItem[] {
  if (product === "smart-ppt") {
    return smartPptSidebarNavigation;
  }
  if (product === "smart-resume") {
    return smartResumeSidebarNavigation;
  }

  return productNavigation[product];
}

export function getFirstAvailableHref(item: NavItem): string | undefined {
  if (item.href) {
    return item.href;
  }

  for (const child of item.items || []) {
    const href = getFirstAvailableHref(child);
    if (href) {
      return href;
    }
  }

  return undefined;
}

export function getFlattenedNav(nav: NavItem[]): { title: string; href: string }[] {
  let flat: { title: string; href: string }[] = [];
  for (const item of nav) {
    if (item.href) {
      flat.push({ title: item.title, href: item.href });
    }
    if (item.items) {
      flat = flat.concat(getFlattenedNav(item.items));
    }
  }
  return flat;
}
